<script setup>
import { computed } from "vue";
import stores from "@/stores";
import DepartmentDropdown from "./DepartmentDropdown.vue";
import InteractionStatusDropdown from "./InteractionStatusDropdown.vue";
import {
  BASE_TIMEZONE,
  InteractionSourceEnum,
  statusToOptionsMapping,
} from "@/utils/constants.js";
import AddTagsDropdown from "@/components/AddTagsDropdown.vue";
import IconEmail from "@/components/icons/IconEmail.vue";
import { formatInTimeZone } from "@/utils/dates";
import { truncateString } from "@/utils/utils.js";
import { TAB_VALUE } from "@/constants/interaction";

const MAX_TITLE_LENGTH = 70;
const MAX_USER_NAME_WITH_LOCATION_LENGTH = 20;
const MAX_TIME_LENGTH = 20;

const userStore = stores.useUserStore();
const unitStore = stores.useUnitStore();
const teamStore = stores.useTeamStore();
const deptStore = stores.useDepartmentStore();

const emit = defineEmits([
  "show-unknown-user-ticket-modal",
]);

const props = defineProps({
  allowHover: {
    type: Boolean,
    default: true,
  },
  shouldDisplayChannelIcon: {
    type: Boolean,
    default: true,
  },
  threadDetails: {
    type: Object,
    default: {},
  },
  showTags: {
    type: Boolean,
    default: false,
  },
  showCountersAndTrainingState: {
    type: Boolean,
    default: true,
  },
  paddingSize: {
    type: String,
    default: "normal",
  },
  tagsOptions: {
    type: Array,
    default: [],
  },
  statusDropdownOptions: {
    type: Object,
    default: {},
  },
});

const openModal = async () => {
  emit(
    "show-unknown-user-ticket-modal",
    props.threadDetails._id,
  );
};

const user = userStore.allDict[props.threadDetails?.user_id];
let userName = `${user?.first_name || ""} ${user?.last_name || ""}`;
if (userName.trim() === "") {
  userName = user?.email || user?.username || "no user name";
}
const unit_name = unitStore.allDict[user?.unit_id]?.store_name || "no unit";

const hasInteractionDetails = computed(() => {
  return Object.keys(interactionDetails.value).length;
});

const interactionDetails = computed(() => {
  if (
    props.threadDetails?.source !== InteractionSourceEnum.UNKNOWN_USER_TICKET ||
    props.threadDetails?.interactions?.length != 1
  ) {
    return {};
  }
  return props.threadDetails.interactions[0];
});

const extractedInfo = computed(() => {
  const query = interactionDetails.value.query;
  try {
    const parsed = JSON.parse(query);
    return parsed;
  }
  catch (error) {
    console.error("Error parsing JSON", query);
    return {};
  }
});

const computedTitle = computed(() => {
  let title = extractedInfo.value.subject;
  if (!!title) return title;

  title = extractedInfo.value.processed;
  if (!title) return "";

  if (title && title.length <= 50) return title;
  return title.slice(0, 50) + "...";
});

const formattedCreatedAt = computed(() => {
  if (!hasInteractionDetails.value || !props.threadDetails?.created_at) {
    return "";
  }
  const formattedValue = formatInTimeZone(
    props.threadDetails.created_at,
    BASE_TIMEZONE,
    "hh:mm A, DD MMM YYYY"
  );

  // yeah, bad practice tampering with a prop
  props.threadDetails.formattedCreatedAt = formattedValue;
  return formattedValue;
});

const currentIcon = IconEmail;

</script>

<template>
  <div
    class="interaction-card"
    :class="{
      'allow-hover': allowHover,
    }"
    @click="() => openModal()"
  >
    <div class="d-flex align-items-center">
      <div v-if="shouldDisplayChannelIcon" class="position-relative">
        <icon-marker class="position-relative" size="big">
          <component
            :is="currentIcon"
            :width="18"
            :height="18"
            v-if="currentIcon"
          />
        </icon-marker>

        <div class="interaction-arrow">
          <IconIncomingArrow />
        </div>
      </div>

      <div
        :class="{
          'ms-4': shouldDisplayChannelIcon,
        }"
        class="d-flex justify-content-between align-items-center interaction-content flex-grow-1"
      >
        <div class="interaction-content">
          <div
            class="interaction-title text-neutral-450"
          >
            <span>{{ computedTitle }}</span>
          </div>

          <div
            class="interaction-subtitle text-neutral-300 d-flex align-items-center column-gap-1"
          >
            <span
              class="text-decoration-underline"
              v-b-tooltip.hover.top="threadDetails.short_id"
            >
              #{{ threadDetails.short_id }}
            </span>
            <IconFrame />
            <span>
              {{ extractedInfo.reply_to?.send_response_to }}
            </span>
            <IconFrame />
            <span>
              {{ formattedCreatedAt }}
            </span>
          </div>
        </div>

        <div
          class="d-flex show-state"
          style="min-width: 550px"
          :class="{ 'my-column-gap-4': !showCountersAndTrainingState }"
        >
          <div class="flex-item department-name">
            {{ deptStore.allDict[threadDetails?.department]?.name }}
          </div>

          <div class="flex-item received-email">
            {{ extractedInfo.allow_unknown_users_emails?.[0] || "--" }}
          </div>

        </div>
      </div>
    </div>

  </div>
</template>

<style scoped lang="scss">
.interaction-arrow {
  border-radius: 100px;
  border: 2px solid $neutral-0;
  background: $neutral-150;
  display: flex;
  padding: var(--padding-xs, 4px);
  align-items: center;
  gap: 10px;
  width: fit-content;
  position: absolute;
  left: -5px;
  bottom: -5px;
}

.interaction-card {
  padding: 1rem 1rem 1rem 1.5rem;
  background-color: white;
  cursor: pointer;
  border-bottom: 1px solid $neutral-150;

  .show-state {
    justify-content: flex-end !important;
  }

  &.allow-hover {
    &:hover {
      background: $neutral-50;
    }
  }

  &.selected {
    background: var(--primary-0) !important;
  }

  :deep(.icon-marker) {
    background: $neutral-0;
  }

  .department-name,
  .received-email {
    font-size: $font-size-xs;
  }

  &.has-alert-marker {
    background: $neutral-50;

    :deep(.department-dropdown) {
      .btn {
        background: $neutral-50;
      }
    }

    .three-dots-dropdown {
      :deep(.dropdown) {
        .three-dots-vertical-icon {
          path {
            fill: $neutral-400;
          }
        }
      }
    }
  }

  .transit-icon {
    position: absolute;
    right: 22px;
    top: 20px;
    border: 2px solid $bluish-gray-100;
    background: $bluish-gray-300;
    color: $bluish-gray-900;
    border-radius: 50%;
  }

  .circle-fill {
    position: absolute;
    width: 12px;
    height: 12px;
    left: 34px;
    bottom: 34px;
    color: var(--primary-250);
  }

  .interaction-title {
    font-size: $font-size-md;
    color: $neutral-450;

    &.bold-text {
      font-weight: bold;
    }

    .feedback-icon {
      margin-left: 0.75rem;

      :deep(path) {
        fill: $neutral-300;
      }
    }
  }

  .interaction-content {
    min-width: 425px;
    margin-right: 20px;
  }

  .interaction-subtitle {
    font-size: 14px;
    //> span:nth-child(1n) {
    //  white-space: nowrap;
    //  overflow: hidden;
    //  text-overflow: ellipsis;
    //  text-align: left;
    //}
  }

  .flex-item {
    width: 15rem;
    // flex-grow: 1;
  }

  .training-assignee {
    min-width: 50px;
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .messages-counts,
  .comments-counts,
  .training-state {
    min-width: 50px;
    :deep(svg) {
      path {
        fill: $neutral-300;
      }
    }

    .count {
      color: $neutral-300;
    }
    &:hover {
      :deep(svg) {
        path {
          fill: $neutral-400;
        }
      }

      .count {
        color: $neutral-400;
      }
    }
  }
}
</style>
